import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Calendar,
  MapPin,
  Download,
  Share2,
  ArrowLeft,
  Check,
  Loader2,
  Star,
  Sun,
  Moon,
  CalendarPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { PremiumUpsell } from '@/components/PremiumUpsell';
import { SEOHead } from '@/components/SEOHead';
import { SHARED_PAGE_SHELL_SLUG, sharedCalendarDescription, sharedCalendarTitle } from '@/lib/sharedPage';
import { trackEvent } from '@/utils/analytics';
import { track } from '@/lib/analytics';
import { VisibilityBars } from '@/components/calendar/VisibilityBars';
import { calculateMonthData, DayData, getVisibilityRating as getVisibilityRatingFromData } from '@/utils/calendarData';
import { getMoonPhaseEmoji } from '@/utils/moonCalculations';
import { generateYearlyICS, downloadICSFile } from '@/utils/icsExport';
import CalendarImportGuideModal from '@/components/CalendarImportGuideModal';
import { useIsMobile } from '@/hooks/use-mobile';
import { NearbyWorkshopsBanner } from '@/components/workshops/NearbyWorkshopsBanner';
import { NearbyWorkshops } from '@/components/workshops/NearbyWorkshops';

interface CalendarData {
  id: string;
  slug: string;
  location_name: string;
  latitude: number;
  longitude: number;
  year: number;
  view_count: number;
  download_count: number;
  created_at: string;
}

interface BestNight {
  date: number;
  fullDate: string;
  moonPhase: number;
  rating: number;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function getAllDaysForMonth(year: number, month: number, latitude: number, longitude: number, locationName: string): BestNight[] {
  const monthData = calculateMonthData(
    { name: locationName, latitude, longitude },
    month,
    year
  );
  
  return monthData.map(day => ({
    date: day.date,
    fullDate: day.fullDate,
    moonPhase: day.moonPhase,
    rating: getVisibilityRatingFromData(day),
  }));
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}


export default function SharedCalendar() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isSubscribed } = useAuth();
  
  const [calendar, setCalendar] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(0);
  const [showImportGuide, setShowImportGuide] = useState(false);
  const [icsFilename, setIcsFilename] = useState('');
  useEffect(() => {
    async function fetchCalendar() {
      if (!slug) {
        navigate('/planner');
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc('get_shared_calendar_by_slug', { calendar_slug: slug });

        if (error) throw error;

        const calendarData = Array.isArray(data) ? data[0] : data;

        if (!calendarData) {
          toast.error('Calendar not found');
          navigate('/planner');
          return;
        }

        setCalendar(calendarData);

        // Increment view count via secure function
        await supabase.rpc('increment_calendar_view', { calendar_slug: slug });

        trackEvent('annual_calendar_view', { slug, location: calendarData.location_name });
      } catch (error) {
        console.error('Error fetching calendar:', error);
        toast.error('Failed to load calendar');
        navigate('/planner');
      } finally {
        setLoading(false);
      }
    }

    fetchCalendar();
  }, [slug, navigate]);

  const yearlyData = useMemo(() => {
    if (!calendar) return [];
    
    return MONTH_NAMES.map((name, index) => ({
      name,
      index,
      days: getAllDaysForMonth(calendar.year, index, calendar.latitude, calendar.longitude, calendar.location_name),
      firstDayOfWeek: getFirstDayOfMonth(calendar.year, index),
    }));
  }, [calendar]);

  // Calculate detailed astronomical data for the selected month
  const detailedMonthData = useMemo(() => {
    if (!calendar) return [];
    
    const location = {
      name: calendar.location_name,
      latitude: calendar.latitude,
      longitude: calendar.longitude
    };
    
    return calculateMonthData(location, selectedMonth, calendar.year);
  }, [calendar, selectedMonth]);

  // Use shared visibility rating function
  const getDetailedVisibilityRating = getVisibilityRatingFromData;

  // Filter best nights for selected month (rating >= 5)
  const bestNightsForMonth = useMemo(() => {
    return detailedMonthData.filter(day => getDetailedVisibilityRating(day) >= 5);
  }, [detailedMonthData]);

  // Count best nights per month for badges
  const bestNightsPerMonth = useMemo(() => {
    if (!calendar) return [];
    
    const location = {
      name: calendar.location_name,
      latitude: calendar.latitude,
      longitude: calendar.longitude
    };
    
    return MONTH_NAMES.map((_, monthIndex) => {
      const monthData = calculateMonthData(location, monthIndex, calendar.year);
      return monthData.filter(day => getDetailedVisibilityRating(day) >= 5).length;
    });
  }, [calendar]);

  const handleDownloadPDF = async () => {
    if (!slug) return;
    
    setDownloading(true);
    try {
      // Use fetch directly to properly handle binary PDF response
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/generate-calendar-pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ slug }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Get response as ArrayBuffer to preserve binary data
      const arrayBuffer = await response.arrayBuffer();
      
      // Create blob from ArrayBuffer
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${calendar?.location_name.replace(/[^a-zA-Z0-9]/g, '-')}-${calendar?.year}-milky-way-calendar.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      trackEvent('annual_calendar_download', { slug, location: calendar?.location_name });
      if (typeof calendar?.year === 'number') {
        track('calendar_pdf_download', { year: calendar.year });
      }
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${calendar?.location_name} - Milky Way Calendar ${calendar?.year}`,
          text: `Best nights for Milky Way photography at ${calendar?.location_name} in ${calendar?.year}`,
          url: window.location.href,
        });
      } catch {
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  const handleAddToCalendar = () => {
    if (!calendar) return;
    const { icsContent, eventCount, filename } = generateYearlyICS(
      { name: calendar.location_name, latitude: calendar.latitude, longitude: calendar.longitude },
      calendar.year,
      4,
      // The core direction line is Premium only (ERI-2596).
      { includeDirection: isSubscribed },
    );
    if (eventCount === 0) {
      toast.error('No visible nights found for this calendar.');
      return;
    }
    downloadICSFile(icsContent, filename);
    setIcsFilename(filename);
    setShowImportGuide(true);
    trackEvent('ics_download', {
      slug,
      location: calendar.location_name,
      year: calendar.year,
      source: 'shared_calendar',
      event_count: eventCount,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        {/* The build prerenders this state as the shell middleware.ts serves
            for every shared calendar (ERI-2783). The shell itself is not a page
            to index; Head renders nothing in the body, so hydration still matches. */}
        {slug === SHARED_PAGE_SHELL_SLUG && (
          <SEOHead
            title="Shared Milky Way Calendar | Milky Way Planner"
            description="A shared annual Milky Way calendar from Milky Way Planner, with nightly visibility ratings, moon phases, and galactic core timing for one location."
            noindex
          />
        )}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-1">Milky Way Calendar</h1>
          <Skeleton className="h-12 w-64 mb-4" />
          <Skeleton className="h-6 w-48 mb-8" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!calendar) {
    return null;
  }

  return (
    <>
      <SEOHead
        title={sharedCalendarTitle(calendar.location_name, calendar.year)}
        description={sharedCalendarDescription(calendar.location_name, calendar.year)}
        keywords={`milky way ${calendar.location_name}, milky way calendar ${calendar.year}, astrophotography ${calendar.location_name}, best nights milky way, galactic core visibility`}
        canonicalUrl={`/calendar/${calendar.slug}`}
      />
      
      <div className="min-h-screen bg-background">
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          {/* Intro Banner */}
          <div className="mb-8 rounded-[4px] border border-border p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white font-semibold px-3 py-1">
                    FREE
                  </Badge>
                  <span className="text-lg font-semibold text-foreground">Annual Milky Way Calendar</span>
                </div>
                <p className="text-muted-foreground max-w-xl">
                  See the best nights for astrophotography all year long. This calendar shows moon phases and Milky Way visibility for every night of the year.
                </p>
                {!isSubscribed && (
                <div className="pt-2 space-y-2 text-sm">
                  <p className="font-medium text-foreground">Get Premium for $25/year to unlock:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-muted-foreground">
                    <span className="flex items-center gap-2"><span className="text-gold">✦</span> Save unlimited locations</span>
                    <span className="flex items-center gap-2"><span className="text-gold">✦</span> Create unlimited calendars</span>
                    <span className="flex items-center gap-2"><span className="text-gold">✦</span> Moon phase planner</span>
                    <span className="flex items-center gap-2"><span className="text-gold">✦</span> Multi-stop trip planning</span>
                  </div>
                </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/">
                  <Button variant="outline" className="w-full sm:w-auto">
                    <Calendar className="w-4 h-4 mr-2" />
                    Create Your Own
                  </Button>
                </Link>
                {!isSubscribed && (
                <Link to="/register">
                  <Button variant="premium" className="w-full sm:w-auto">
                    <Star className="w-4 h-4 mr-2" />
                    Get Premium — $25/year
                  </Button>
                </Link>
                )}
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-1">
                {calendar.year} Annual Milky Way Calendar
              </h1>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span className="font-medium text-foreground">{calendar.location_name}</span>
                <span className="text-sm">
                  ({calendar.latitude.toFixed(4)}°, {calendar.longitude.toFixed(4)}°)
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={handleDownloadPDF}
                disabled={downloading}
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download PDF
              </Button>

              <Button variant="outline" onClick={handleAddToCalendar}>
                <CalendarPlus className="w-4 h-4 mr-2" />
                Export to My Calendar
              </Button>

              <Button variant="outline" onClick={handleShare}>
                {copied ? (
                  <Check className="w-4 h-4 mr-2" />
                ) : (
                  <Share2 className="w-4 h-4 mr-2" />
                )}
                Share
              </Button>
            </div>
          </div>

          {/* Nearby Workshops Banner */}
          <div className="mb-6">
            <NearbyWorkshopsBanner
              latitude={calendar.latitude}
              longitude={calendar.longitude}
              locationName={calendar.location_name}
            />
          </div>

          {/* Tabs */}
          <Tabs defaultValue="calendar" className="mb-12">
            <TabsList className="grid w-fit grid-cols-2 mx-auto mb-6">
              <TabsTrigger value="calendar" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Full Calendar
              </TabsTrigger>
              <TabsTrigger value="best-nights" className="flex items-center gap-2">
                <Star className="w-4 h-4" />
                Best Nights
              </TabsTrigger>
            </TabsList>

            <TabsContent value="calendar">
              {/* Color Legend */}
              <div className="flex flex-wrap items-center gap-4 mb-6 p-4 rounded-lg bg-secondary/20 border border-border/50">
                <span className="text-sm font-medium text-foreground">Visibility Rating:</span>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-sm bg-emerald-600"></div>
                  <span className="text-sm text-muted-foreground">Excellent (8-10)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-sm bg-green-500"></div>
                  <span className="text-sm text-muted-foreground">Very Good (6-7)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-sm bg-lime-400"></div>
                  <span className="text-sm text-muted-foreground">Good (4-5)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-sm bg-amber-300"></div>
                  <span className="text-sm text-muted-foreground">Fair (2-3)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-sm bg-muted/30 border border-border/50"></div>
                  <span className="text-sm text-muted-foreground">Poor/None</span>
                </div>
              </div>

              {/* 12-month grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {yearlyData.map((month) => (
                  <Card key={month.index} className="glass-card border-border/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-semibold text-foreground">
                        {month.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {/* Day of week headers */}
                      <div className="grid grid-cols-7 gap-0.5 mb-1">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                          <div key={i} className="text-center text-xs text-muted-foreground font-medium py-1">
                            {day}
                          </div>
                        ))}
                      </div>
                      
                      {/* Calendar grid */}
                      <div className="grid grid-cols-7 gap-0.5">
                        {/* Empty cells for days before month starts */}
                        {Array.from({ length: month.firstDayOfWeek }, (_, i) => (
                          <div key={`empty-${i}`} className="aspect-square" />
                        ))}
                        
                        {/* Day cells */}
                        {month.days.map((day) => {
                          const hasVisibility = day.rating > 0;
                          const bgColor = day.rating >= 8 
                            ? 'bg-emerald-600 text-white' 
                            : day.rating >= 6 
                              ? 'bg-green-500' 
                              : day.rating >= 4 
                                ? 'bg-lime-400' 
                                : day.rating >= 2 
                                  ? 'bg-amber-300' 
                                  : '';
                          
                          return (
                            <div
                              key={day.date}
                              className={`aspect-square flex items-center justify-center text-xs rounded-sm ${
                                hasVisibility 
                                  ? `${bgColor} text-foreground font-medium` 
                                  : 'text-muted-foreground/50'
                              }`}
                              title={hasVisibility ? `Rating: ${day.rating}/10` : 'No visibility'}
                            >
                              {day.date}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="best-nights">
              {/* Month navigation tabs */}
              <Tabs value={selectedMonth.toString()} onValueChange={(value) => setSelectedMonth(parseInt(value))}>
                <TabsList className="grid grid-cols-6 lg:grid-cols-12 gap-1 h-auto p-1 mb-6">
                  {MONTH_NAMES.map((month, index) => (
                    <TabsTrigger 
                      key={index} 
                      value={index.toString()}
                      className="text-xs px-2 py-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                    >
                      {month.slice(0, 3)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              {/* Best nights table for selected month */}
              {bestNightsForMonth.length === 0 ? (
                <Card className="glass-card border-border/50 p-8 text-center">
                  <p className="text-muted-foreground">
                    No best nights (rating ≥5) available for {MONTH_NAMES[selectedMonth]}.
                  </p>
                </Card>
              ) : isMobile ? (
                /* Mobile card view */
                <div className="space-y-4">
                  {bestNightsForMonth.map((day) => (
                    <Card key={day.date} className="glass-card border-border/50 shadow-md">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {/* Date and Visibility */}
                          <div className="flex justify-between items-center">
                            <h3 className="font-semibold text-lg">{day.fullDate}</h3>
                            <div className="flex items-center gap-3">
                              <div className="text-center">
                                <div className="text-xs text-muted-foreground mb-1">Visibility</div>
                                <VisibilityBars rating={getDetailedVisibilityRating(day)} />
                              </div>
                              <span className="text-2xl">{getMoonPhaseEmoji(day.moonPhase, day.moonPhaseCycle)}</span>
                            </div>
                          </div>
                          
                          {/* Sun Times */}
                          <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                            <div className="text-center">
                              <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
                                <Sun className="w-4 h-4" />
                                <span className="text-xs font-medium">Sunrise</span>
                              </div>
                              <p className="text-sm font-mono">{day.sunrise}</p>
                            </div>
                            <div className="text-center">
                              <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
                                <Sun className="w-4 h-4" />
                                <span className="text-xs font-medium">Sunset</span>
                              </div>
                              <p className="text-sm font-mono">{day.sunset}</p>
                            </div>
                          </div>

                          {/* Moon Times */}
                          <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 dark:bg-muted/30 rounded-lg">
                            <div className="text-center">
                              <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                                <Moon className="w-4 h-4" />
                                <span className="text-xs font-medium">Moonrise</span>
                              </div>
                              <p className="text-sm font-mono">{day.moonrise}</p>
                            </div>
                            <div className="text-center">
                              <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                                <Moon className="w-4 h-4" />
                                <span className="text-xs font-medium">Moonset</span>
                              </div>
                              <p className="text-sm font-mono">{day.moonset}</p>
                            </div>
                          </div>

                          {/* Milky Way Times */}
                          <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                            <div className="text-center">
                              <div className="flex items-center justify-center gap-1 text-gold-foreground dark:text-gold-light mb-1">
                                <span className="text-sm">✨</span>
                                <span className="text-xs font-medium">GC Start</span>
                              </div>
                              <p className={`text-sm font-mono ${day.galacticCoreVisible ? "text-gold-foreground dark:text-gold-light font-medium" : "text-muted-foreground"}`}>
                                {day.galacticCoreStart}
                              </p>
                            </div>
                            <div className="text-center">
                              <div className="flex items-center justify-center gap-1 text-gold-foreground dark:text-gold-light mb-1">
                                <span className="text-sm">✨</span>
                                <span className="text-xs font-medium">GC End</span>
                              </div>
                              <p className={`text-sm font-mono ${day.galacticCoreVisible ? "text-gold-foreground dark:text-gold-light font-medium" : "text-muted-foreground"}`}>
                                {day.galacticCoreEnd}
                              </p>
                            </div>
                          </div>

                          {/* Moon Phase Percentage */}
                          <div className="text-center p-2 bg-secondary/30 rounded-lg">
                            <span className="text-xs text-muted-foreground">Moon Illumination: </span>
                            <span className="text-sm font-medium">{Math.round(day.moonPhase * 100)}%</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                /* Desktop table view */
                <Card className="glass-card border-border/50 shadow-lg overflow-hidden">
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border/50 bg-secondary/20">
                            <TableHead className="text-foreground font-medium py-4 min-w-[90px] sticky left-0 bg-secondary/20 z-10">Date</TableHead>
                            <TableHead className="text-amber-600 font-medium min-w-[70px]">
                              <div className="flex items-center">
                                <Sun className="w-3 h-3 mr-1" />
                                <span className="hidden sm:inline">Sunrise</span>
                                <span className="sm:hidden">Rise</span>
                              </div>
                            </TableHead>
                            <TableHead className="text-amber-600 font-medium min-w-[70px]">
                              <div className="flex items-center">
                                <Sun className="w-3 h-3 mr-1" />
                                <span className="hidden sm:inline">Sunset</span>
                                <span className="sm:hidden">Set</span>
                              </div>
                            </TableHead>
                            <TableHead className="text-muted-foreground font-medium min-w-[70px] hidden md:table-cell">
                              <div className="flex items-center">
                                <Moon className="w-3 h-3 mr-1" />
                                <span className="hidden lg:inline">Moonrise</span>
                                <span className="lg:hidden">Rise</span>
                              </div>
                            </TableHead>
                            <TableHead className="text-muted-foreground font-medium min-w-[70px] hidden md:table-cell">
                              <div className="flex items-center">
                                <Moon className="w-3 h-3 mr-1" />
                                <span className="hidden lg:inline">Moonset</span>
                                <span className="lg:hidden">Set</span>
                              </div>
                            </TableHead>
                            <TableHead className="text-space-gray font-medium min-w-[80px] hidden lg:table-cell">
                              <div className="flex items-center">
                                <span className="mr-1">✨</span>
                                <span className="hidden xl:inline whitespace-normal">GC Start</span>
                                <span className="xl:hidden">GC Start</span>
                              </div>
                            </TableHead>
                            <TableHead className="text-space-gray font-medium min-w-[80px] hidden lg:table-cell">
                              <div className="flex items-center">
                                <span className="mr-1">✨</span>
                                <span className="hidden xl:inline whitespace-normal">GC End</span>
                                <span className="xl:hidden">GC End</span>
                              </div>
                            </TableHead>
                            <TableHead className="text-muted-foreground font-medium min-w-[50px] text-center hidden sm:table-cell">
                              <span className="hidden md:inline">Moon %</span>
                              <span className="md:hidden">%</span>
                            </TableHead>
                            <TableHead className="text-muted-foreground font-medium min-w-[50px] text-center">
                              <span className="hidden sm:inline">Phase</span>
                              <span className="sm:hidden">🌙</span>
                            </TableHead>
                            <TableHead className="text-muted-foreground font-medium min-w-[80px] text-center sticky right-0 bg-secondary/20 z-10">
                              Visibility
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bestNightsForMonth.map((day) => (
                            <TableRow key={day.date} className="border-border/30 hover:bg-secondary/20 transition-colors">
                              <TableCell className="text-foreground font-medium py-3 sticky left-0 bg-background z-10 border-r border-border/30">
                                <div className="text-sm">{day.fullDate}</div>
                              </TableCell>
                              <TableCell className="text-muted-foreground text-xs">{day.sunrise}</TableCell>
                              <TableCell className="text-muted-foreground text-xs">{day.sunset}</TableCell>
                              <TableCell className="text-muted-foreground text-xs hidden md:table-cell">{day.moonrise}</TableCell>
                              <TableCell className="text-muted-foreground text-xs hidden md:table-cell">{day.moonset}</TableCell>
                              <TableCell className={`${day.galacticCoreVisible ? "text-space-gray font-medium" : "text-muted-foreground"} text-xs hidden lg:table-cell`}>
                                {day.galacticCoreStart}
                              </TableCell>
                              <TableCell className={`${day.galacticCoreVisible ? "text-space-gray font-medium" : "text-muted-foreground"} text-xs hidden lg:table-cell`}>
                                {day.galacticCoreEnd}
                              </TableCell>
                              <TableCell className="text-muted-foreground text-center text-xs hidden sm:table-cell">{Math.round(day.moonPhase * 100)}%</TableCell>
                              <TableCell className="text-xl text-center">
                                {getMoonPhaseEmoji(day.moonPhase, day.moonPhaseCycle)}
                              </TableCell>
                              <TableCell className="text-center sticky right-0 bg-background z-10 border-l border-border/30">
                                <VisibilityBars rating={getDetailedVisibilityRating(day)} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>

          {/* Nearby Workshop Cards */}
          <div className="mb-8">
            <NearbyWorkshops
              latitude={calendar.latitude}
              longitude={calendar.longitude}
              locationName={calendar.location_name}
            />
          </div>

          {/* Create your own CTA */}
          <div className="text-center mb-12 p-8 rounded-xl bg-secondary/20 border border-border/50">
            <h2 className="text-2xl font-semibold text-foreground mb-3">
              Create Your Own Calendar
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Plan your Milky Way photography for any location in the world. 
              Get personalized best nights based on moon phases and galactic core visibility.
            </p>
            <Link to="/planner">
              <Button variant="outline" size="lg">
                <Calendar className="w-4 h-4 mr-2" />
                Create Annual Calendar
              </Button>
            </Link>
          </div>

          {/* Premium Upsell */}
          {!isSubscribed && <PremiumUpsell onPurchase={() => {}} />}
        </main>

      </div>

      <CalendarImportGuideModal
        isOpen={showImportGuide}
        onClose={() => setShowImportGuide(false)}
        fileName={icsFilename}
      />
    </>
  );
}
